import { useMemo } from 'react';

import { proposalHasTrigger } from '@/components/proposals/ActionOverview.helpers';
import { isWideMixedGroup } from '@/components/proposals/shared';
import { groupActionsByType } from '@/lib/proposals/utils/actionGrouping';

import type { ProposalDetail, ProposalResolution } from '../core/types';

// Resolutions after which a LinkedIn send action executes and creates an
// outbound row. Before approval no row exists; the delivery hook uses this to
// bootstrap-poll so a freshly-queued row is discovered without a manual refresh.
const RESOLUTIONS_EXPECTING_DELIVERY: ReadonlySet<ProposalResolution> = new Set(
  ['approved', 'executing']
);

// A LinkedIn outbound row (and thus a delivery badge) is only ever created for
// a proposal that sends on the LinkedIn channel, so delivery tracking is gated
// on this — ordinary CRM / Gmail / calendar proposals then never fetch or
// bootstrap-poll the delivery-status endpoint. The `linkedin_send` action TYPE
// is not yet part of the ProposedActionTypeName union, so the proposal's source
// channel is the coarsest available eligibility signal: it admits LinkedIn
// proposals that carry no send (or mix a send with non-outbound actions). Those
// cost at most a bounded bootstrap window — the delivery hook stops polling the
// moment the first real outbound row appears, rather than waiting for a row per
// fed action id (see computeDeliveryPollInterval).
const LINKEDIN_CHANNEL = 'linkedin';

export interface ProposalContentDerivedState {
  isTableProposal: boolean;
  hasTrigger: boolean;
  deliveryActionIds: number[];
  expectsDeliveryRows: boolean;
}

/**
 * Derives layout and delivery-tracking state from a proposal for
 * ProposalContent. Extracted here to keep ProposalContent.tsx under the
 * 300-line convention.
 */
export function useProposalContentDerivedState(
  proposal: ProposalDetail,
  hasCustomUI: ProposalDetail['ui_definition'] | boolean | null | undefined,
  visibleProposal: ProposalDetail
): ProposalContentDerivedState {
  // Detect if this proposal renders purely as WIDE tables (for flex-fill
  // full-width layout). `viewType: 'table'` groups always carry real
  // property/entity columns, so they always qualify. `viewType: 'mixed'`
  // groups only qualify when they render as the wide homogeneous shape
  // (`isWideMixedGroup`) — their default rendering is a single `Action`
  // description column, which has nothing to fill the extra width with and
  // would just stretch this card and ActionOverview/TriggerPreview above it
  // (sharing this same outer wrapper) for no visual benefit. Proposals with
  // non-table actions mixed in, or with only narrow tables, stay in the
  // standard 48rem column for readability.
  const isTableProposal = useMemo(() => {
    if (hasCustomUI || visibleProposal.actions.length <= 1) {
      return false;
    }
    const groups = groupActionsByType(visibleProposal.actions);
    return (
      groups.length > 0 &&
      groups.every(
        g =>
          g.viewType === 'table' ||
          (g.viewType === 'mixed' && isWideMixedGroup(g.actions))
      )
    );
  }, [hasCustomUI, visibleProposal.actions]);

  // Determine trigger display via the shared helper so this and the trigger
  // shortcut consumers (useTriggerShortcutButtons) cannot diverge. It scans the
  // ORIGINAL (unfiltered) action list, not visibleProposal.actions: hiding
  // no-op update actions must not disable the trigger preview.
  const hasTrigger = proposalHasTrigger(proposal);

  // Only LinkedIn-channel proposals ever produce an outbound delivery row, so
  // only they feed action ids into the delivery hook. For every other proposal
  // this stays empty, which disables the delivery-status query entirely (no
  // fetch, no bootstrap poll).
  const isLinkedInProposal =
    proposal.source_context?.channel === LINKEDIN_CHANNEL;

  const deliveryActionIds = useMemo(
    () => (isLinkedInProposal ? proposal.actions.map(a => a.id) : []),
    [isLinkedInProposal, proposal.actions]
  );

  // Once a LinkedIn proposal is approved/executing, its outbound row is imminent
  // — let the delivery hook bootstrap-poll so a freshly-queued row surfaces
  // promptly. Non-LinkedIn proposals never bootstrap-poll.
  const expectsDeliveryRows =
    isLinkedInProposal &&
    RESOLUTIONS_EXPECTING_DELIVERY.has(proposal.resolution);

  return {
    isTableProposal,
    hasTrigger,
    deliveryActionIds,
    expectsDeliveryRows,
  };
}
